import type { PaginationState } from '@tanstack/table-core';
import axios from 'axios';
import dayjs from 'dayjs';

import type { ODataPageResult } from '@apple/utils/api/odata';
import { createODataPageResultSchema } from '@apple/utils/api/odata';

import type {
	ActivityLogEntityType,
	ActivityLogFilters,
	ActivityLogFiltersExtended,
	ActivityLogListing,
} from '../models/activity';
import { activityLogListingSchema } from '../models/activity';

const activityLogResultsSchema =
	createODataPageResultSchema<ActivityLogListing>(activityLogListingSchema);

const activityLogListItemArraySchema = activityLogListingSchema.array();

/** @see AppleRetail.Web.Features.Events.AppleRetailActivityLogSearchController */
export async function search(
	pagination: PaginationState,
	filters: ActivityLogFiltersExtended,
	signal?: AbortSignal,
): Promise<ODataPageResult<ActivityLogListing>> {
	const response = await axios.get<unknown>('/api/activity-log-search', {
		params: {
			...buildFilter(filters),
			pageNumber: pagination.pageIndex + 1,
			pageSize: pagination.pageSize,
		},
		signal,
	});

	return activityLogResultsSchema.parse(response.data);
}

/** @see AppleRetail.Web.Features.Events.AppleRetailActivityLogSearchController */
export async function getEntries(
	correlationId: string,
	entityType: ActivityLogEntityType,
	limit: number,
	signal?: AbortSignal,
): Promise<ActivityLogListing[]> {
	const response = await axios.get<unknown>('/api/activity-log-search/group', {
		params: {
			correlationId,
			entityType,
			limit,
		},
		signal,
	});

	return activityLogListItemArraySchema.parse(response.data);
}

/** @see AppleRetail.Web.Features.Events.AppleRetailActivityLogSearchController */
export function getUserActivityExportUri(): string {
	return axios.getUri({
		url: '/api/activity-log-search/export',
	});
}

function buildFilter(filters: ActivityLogFiltersExtended) {
	const updatedFilters: ActivityLogFilters = {
		startDate: filters.startDate,
		endDate: filters.endDate,
		userId: filters.userId,
		role: filters.role,
		entity: filters.entity,
		event: filters.event,
	};

	if (filters.dateRange && filters.dateRange !== 'custom') {
		updatedFilters.startDate = dayjs()
			.subtract(filters.dateRange, 'days')
			.startOf('day')
			.toDate();
		updatedFilters.endDate = undefined;
	}

	return updatedFilters;
}
